import { useEffect, useState } from "react";
import { Plus, Phone, User, Pencil, Trash2, Search, Download } from "lucide-react";
import * as XLSX from "xlsx";
import CRM_API from "../../api/crmApi";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../components/ui/table";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../../components/ui/dropdown-menu";
import PageHeader from "../../components/admin/PageHeader";

export default function CrmLeads({ onNavigate }) {
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");

  const crmRole = localStorage.getItem("crm_role");
  const isAdmin = crmRole === "admin";

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLeads(); }, []);

  const fetchLeads = async () => {
    try {
      const res = await CRM_API.get("/leads");
      setLeads(res.data);
    } catch (error) { console.log("Error loading leads", error); }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await CRM_API.delete(`/leads/${id}`);
      fetchLeads();
    } catch (error) { console.log("Delete failed", error); }
  };

  const filteredLeads = leads.filter((l) =>
    l.student_name?.toLowerCase().includes(search.toLowerCase()) ||
    l.student_contact?.includes(search)
  );

  const getExportRows = () =>
    filteredLeads.map((l) => ({
      "Name": l.student_name || "",
      "Mobile": l.student_contact || "",
      "Gender": l.gender || "",
      "Course": l.interested_course || "",
      "Status": l.status || "",
      "Temperature": l.temperature || "",
      "Lead Source": l.lead_source || "",
      "Preferred Location": l.interested_location || "",
      "Qualification": l.qualification || "",
      "School / College": l.school_name || "",
      "Year of Passing": l.year_of_passing || "",
      "Parent Name": l.parent_name || "",
      "Parent Contact": l.parent_contact || "",
      "City": l.city || "",
      "State": l.state || "",
      "Country": l.country || "",
      "Address": l.address || "",
      "Next Followup": l.next_followup || "",
      ...(isAdmin ? { "Assigned To": l.assigned_to_name || "" } : {}),
    }));

  const exportCSV = () => {
    const rows = getExportRows();
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const escape = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [headers.map(escape).join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    const rows = getExportRows();
    if (!rows.length) return;
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leads");
    XLSX.writeFile(wb, `leads_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Lead Management"
        description={`${leads.length} total leads`}
        action={
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline"><Download className="h-4 w-4" /> Export</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={exportCSV}>Export as CSV</DropdownMenuItem>
                <DropdownMenuItem onClick={exportExcel}>Export as Excel (.xlsx)</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={() => onNavigate("crm-add-lead")}><Plus className="h-4 w-4" /> Add Lead</Button>
          </div>
        }
      />

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search by name or mobile"
          className="pl-9"
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-900">All Leads</span>
          <span className="text-xs text-gray-400">{filteredLeads.length} records</span>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Mobile</TableHead>
              <TableHead>Course</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Temperature</TableHead>
              <TableHead>Next Followup</TableHead>
              {isAdmin && <TableHead>Assigned To</TableHead>}
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredLeads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 8 : 7} className="text-center py-12 text-gray-400">No leads found.</TableCell>
              </TableRow>
            ) : filteredLeads.map((lead) => (
              <TableRow key={lead.id}>
                <TableCell className="font-semibold text-gray-900">{lead.student_name}</TableCell>
                <TableCell className="text-gray-700">{lead.student_contact}</TableCell>
                <TableCell className="text-gray-700 max-w-[160px]"><p className="truncate">{lead.interested_course}</p></TableCell>
                <TableCell><Badge variant="pending">{lead.status}</Badge></TableCell>
                <TableCell><Badge variant={lead.temperature || "secondary"}>{lead.temperature}</Badge></TableCell>
                <TableCell className="text-gray-500 text-xs">{lead.next_followup || "—"}</TableCell>
                {isAdmin && (
                  <TableCell className="text-gray-600 text-xs">{lead.assigned_to_name || "—"}</TableCell>
                )}
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <a
                      href={`tel:${lead.student_contact}`}
                      className="inline-flex items-center gap-1 rounded-md border border-green-200 bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 hover:bg-green-100 transition-colors"
                    >
                      <Phone className="h-3 w-3" /> Call
                    </a>
                    <Button size="sm" variant="outline" onClick={() => onNavigate("crm-lead-profile", lead.id)}>
                      <User className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onNavigate("crm-edit-lead", lead.id)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {isAdmin && (
                      <Button size="sm" variant="destructive" onClick={() => deleteLead(lead.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
